import {
  createContext,
  type HTMLAttributes,
  type ReactNode,
  type SyntheticEvent,
  useContext,
  useId,
  useState,
} from "react";
import MuiTab, { type TabProps as MuiTabProps } from "@mui/material/Tab";
import MuiTabs, { type TabsProps as MuiTabsProps } from "@mui/material/Tabs";

interface TabsState {
  value: TabsValue | false;
  orientation: TabsOrientation;
}

interface TabsActions {
  setValue: (value: TabsValue) => void;
}

interface TabsMeta {
  idPrefix: string;
}

interface TabsContextValue {
  state: TabsState;
  actions: TabsActions;
  meta: TabsMeta;
}

const TabsContext = createContext<TabsContextValue | null>(null);

const useTabsContext = () => {
  const context = useContext(TabsContext);

  if (context == null) {
    throw new Error(
      "TabsList, TabsTrigger, and TabsContent must be rendered inside Tabs"
    );
  }

  return context;
};

const getTabId = (idPrefix: string, value: TabsValue) =>
  `${idPrefix}-tab-${value}`;

const getPanelId = (idPrefix: string, value: TabsValue) =>
  `${idPrefix}-panel-${value}`;

export type TabsValue = string | number;
export type TabsOrientation = "horizontal" | "vertical";

export interface TabsProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "defaultValue" | "onChange"> {
  children: ReactNode;
  /** Active tab value when the component is controlled. */
  value?: TabsValue | false;
  /** Initially active tab value when the component manages its own state. */
  defaultValue?: TabsValue | false;
  /** Called whenever the user selects a different tab. */
  onValueChange?: (value: TabsValue) => void;
  /** Controls keyboard navigation and visual layout direction. */
  orientation?: TabsOrientation;
  /** Optional ID used as the prefix for accessible tab and panel IDs. */
  id?: string;
}

export const Tabs = ({
  children,
  value,
  defaultValue = 0,
  onValueChange,
  orientation = "horizontal",
  id,
  ...props
}: TabsProps) => {
  const generatedId = useId();
  const idPrefix = id ?? `tabs-${generatedId}`;
  const [uncontrolledValue, setUncontrolledValue] =
    useState<TabsValue | false>(defaultValue);
  const selectedValue = value ?? uncontrolledValue;

  const setValue = (nextValue: TabsValue) => {
    if (value === undefined) {
      setUncontrolledValue(nextValue);
    }
    onValueChange?.(nextValue);
  };

  return (
    <TabsContext.Provider
      value={{
        state: { value: selectedValue, orientation },
        actions: { setValue },
        meta: { idPrefix },
      }}
    >
      <div {...props} id={id} data-orientation={orientation}>
        {children}
      </div>
    </TabsContext.Provider>
  );
};

/**
 * Props supported by MUI Tabs, excluding values managed by the parent Tabs.
 */
export type TabsListProps = Omit<
  MuiTabsProps,
  "onChange" | "orientation" | "value"
>;

export const TabsList = (props: TabsListProps) => {
  const {
    state: { value, orientation },
    actions: { setValue },
  } = useTabsContext();

  const handleChange = (_event: SyntheticEvent, nextValue: TabsValue) => {
    setValue(nextValue);
  };

  return (
    <MuiTabs
      {...props}
      value={value}
      orientation={orientation}
      onChange={handleChange}
      data-orientation={orientation}
    />
  );
};

export interface TabsTriggerProps
  extends Omit<MuiTabProps, "aria-controls" | "children" | "id" | "label" | "value"> {
  /** Label content displayed inside the tab button. */
  children: ReactNode;
  /** Value that links this trigger to a TabsContent element. */
  value: TabsValue;
}

export const TabsTrigger = ({
  children,
  value,
  disabled,
  ...props
}: TabsTriggerProps) => {
  const {
    state,
    meta: { idPrefix },
  } = useTabsContext();
  const active = state.value === value;

  return (
    <MuiTab
      {...props}
      value={value}
      disabled={disabled}
      id={getTabId(idPrefix, value)}
      aria-controls={getPanelId(idPrefix, value)}
      label={children}
      data-active={active ? "" : undefined}
      data-disabled={disabled ? "" : undefined}
      data-orientation={state.orientation}
    />
  );
};

export interface TabsContentProps extends HTMLAttributes<HTMLDivElement> {
  /** Content rendered when this panel's value is active. */
  children: ReactNode;
  /** Value that links this content to a TabsTrigger. */
  value: TabsValue;
  /** Keeps inactive content mounted but hidden, preserving its local state. */
  keepMounted?: boolean;
}

export const TabsContent = ({
  children,
  value,
  keepMounted = false,
  ...props
}: TabsContentProps) => {
  const {
    state,
    meta: { idPrefix },
  } = useTabsContext();
  const hidden = state.value !== value;

  if (hidden && !keepMounted) {
    return null;
  }

  return (
    <div
      {...props}
      id={getPanelId(idPrefix, value)}
      role="tabpanel"
      aria-labelledby={getTabId(idPrefix, value)}
      hidden={hidden}
      data-hidden={hidden ? "" : undefined}
      data-orientation={state.orientation}
    >
      {children}
    </div>
  );
};
